import {
  Autocomplete,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { isArray } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Label } from '../Label';

const broderStyle = {
  border: '1px solid #00b4d8',
  borderRadius: 1,
  width: '100%',
};

export type WsAutocompleteInputProps = {
  name: string;
  placeholder: string;
  renderOptions?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  getOptions?: (query: string) => Promise<Array<string>> | Array<string>;
  onNewValueCreate?: (value: string) => void;
  options: Array<string>;
  multiple?: boolean;
  freeSolo?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  label?: string;
  size?: 'small' | 'medium';
  showMandatorySymbol?: boolean;
  textForAddingNew?: string;
  disable?: boolean;
  onChange?: (value: string) => void;
  requireSelection?: boolean;
  requireMultiValueSelection?: boolean;
  isBorderStyle?: boolean;
  disableAddNewOption?: boolean | undefined | string;
};

const WsAutocompleteInput = ({
  name,
  placeholder,
  renderOptions,
  getOptions,
  onNewValueCreate,
  options = [],
  multiple = false,
  freeSolo = false,
  variant = 'outlined',
  label,
  showMandatorySymbol = false,
  size = 'small',
  textForAddingNew,
  disable = false,
  onChange,
  requireSelection = true,
  requireMultiValueSelection = false,
  disableAddNewOption = false,
  isBorderStyle,
  ...props
}: WsAutocompleteInputProps & TextFieldProps) => {
  const { control } = useFormContext();
  const [autocompleteOptions, setAutocompleteOptions] = useState(options);
  const [autocompleteProps, setAutocompleteProps] = useState<
    Partial<WsAutocompleteInputProps> & {
      filterOptions?: (options: Array<string>, state: FilterOptionsState<string>) => string[];
    }
  >({});
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setAutocompleteOptions(options);
    if (getOptions && inputValue) {
      const makeOptionsRequest = async () => {
        const data = await getOptions(inputValue);
        setAutocompleteOptions(data);
      };
      makeOptionsRequest();
    } else if (getOptions && !inputValue) {
      setAutocompleteOptions(options);
    }
    const props: Partial<WsAutocompleteInputProps> & {
      filterOptions?: (options: Array<string>, state: FilterOptionsState<string>) => string[];
    } = {};
    if (renderOptions) {
      props.renderOptions = renderOptions;
    }

    if (getOptions) {
      props.filterOptions = (options, params) => {
        const lowerCaseOptions = options?.map((option) => option.trim().toLowerCase());
        const isOptionAvailable = lowerCaseOptions.some((option) =>
          option.includes(params.inputValue.toLowerCase())
        );
        if (params.inputValue && textForAddingNew && !isOptionAvailable && disableAddNewOption) {
          options.push(`${params.inputValue} ( Not Found - Add ${textForAddingNew})`);
        }
        return options;
      };
    }
    setAutocompleteProps(props);
  }, [inputValue, getOptions, options, renderOptions, textForAddingNew, disableAddNewOption]);

  const handleBlur = () => requireSelection && setInputValue('');

  return (
    <>
      {label && (
        <Label>
          {label}
          {showMandatorySymbol ? ' *' : ''}
        </Label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error, invalid } }) => {
          return (
            <Autocomplete
              {...field}
              {...autocompleteProps}
              id={name}
              multiple={multiple}
              freeSolo={freeSolo}
              size={size}
              sx={isBorderStyle ? broderStyle : undefined}
              options={autocompleteOptions}
              getOptionLabel={(option) =>
                option ? (option.includes('|') ? option.split('|')[0] : option) : ''
              }
              getOptionDisabled={(option) =>
                option.includes('|') ? option.split('|')[1] == 'Inactive' : false
              }
              onBlur={handleBlur}
              onChange={(_, data) => {
                let enteredValue = '';
                if (multiple) {
                  enteredValue = data[data.length - 1];
                } else {
                  enteredValue = data;
                }
                if (
                  onNewValueCreate &&
                  enteredValue &&
                  enteredValue.indexOf(`( Not Found - Add ${textForAddingNew})`) !== -1
                ) {
                  setInputValue('');
                  if (isArray(data) && data.length > 0) data = data.splice(data.length - 1, 1);
                  return onNewValueCreate(
                    enteredValue.replace(`( Not Found - Add ${textForAddingNew})`, '')
                  );
                }
                onChange && onChange(data);
                return field.onChange(data);
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              disabled={disable}
              renderInput={(params) => {
                params.inputProps.onBlur = (event) => {
                  if (
                    (event.target as HTMLInputElement).value.length > 0 &&
                    !requireMultiValueSelection
                  ) {
                    field.value.push((event.target as HTMLInputElement).value);
                    field.onChange([...field.value]);
                    setInputValue('');
                  }
                };
                params.inputProps.onKeyDown = (event) => {
                  if (freeSolo && multiple && !getOptions) {
                    switch (event.key) {
                      case ',':
                      case 'Tab': {
                        event.preventDefault();
                        event.stopPropagation();
                        if ((event.target as HTMLInputElement).value.length > 0) {
                          field.value.push((event.target as HTMLInputElement).value);
                          field.onChange([...field.value]);
                          setInputValue('');
                        }
                        break;
                      }
                      default:
                    }
                  }
                };
                return (
                  <TextField
                    {...params}
                    {...props}
                    placeholder={placeholder}
                    error={invalid}
                    inputRef={ref}
                    variant={variant}
                    helperText={error?.message}
                  />
                );
              }}
            />
          );
        }}
      />
    </>
  );
};

export default WsAutocompleteInput;
